import {
  Badge,
  Button,
  EmptyState,
  Markdown,
  MarkdownEditor,
  Menu,
  type MenuItem,
  Modal,
  PageHeader,
  Skeleton,
  buttonClass,
  useConfirm,
  useToast,
} from '@/components';
import { useAuth } from '@/features/auth/AuthProvider';
import { PollPreview } from '@/features/polls/PollPreview';
import { RespondForm } from '@/features/polls/RespondForm';
import { copyPollLink } from '@/features/polls/api';
import {
  useClosePoll,
  useDeletePoll,
  usePoll,
  usePublishPoll,
  useRetractPollResponse,
  useUpdatePoll,
} from '@/features/polls/hooks';
import { ApiClientError } from '@/lib/api';
import { splitCover } from '@/lib/markdown';
import { A, useNavigate, useParams } from '@solidjs/router';
import { Show, createSignal } from 'solid-js';

export default function PollDetail() {
  const params = useParams<{ pollId: string }>();
  const query = usePoll(() => params.pollId);
  const { session } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const publish = usePublishPoll(params.pollId);
  const close = useClosePoll(params.pollId);
  const remove = useDeletePoll(params.pollId);
  const update = useUpdatePoll(params.pollId);
  const retract = useRetractPollResponse(params.pollId);
  const [submitSlot, setSubmitSlot] = createSignal<HTMLDivElement>();
  const [editingDescription, setEditingDescription] = createSignal(false);
  const [description, setDescription] = createSignal('');

  const actions = (): MenuItem[] => {
    const poll = query.data?.data;
    if (!poll) return [];
    const items: MenuItem[] = [];
    if (poll.status === 'draft' && poll.canManage) {
      items.push(
        { label: 'Edit draft', onSelect: () => navigate(`/polls/${params.pollId}/edit`) },
        {
          label: 'Publish',
          onSelect: async () => {
            if (
              !(await confirm({
                title: 'Publish this poll?',
                body: 'Questions, options, branching, and response settings will be frozen. The description stays editable.',
                confirmLabel: 'Publish',
              }))
            )
              return;
            await publish.mutateAsync(undefined as never);
            toast.success('Poll published');
          },
        },
      );
    }
    const canDeleteAny = session()?.permissions.has('polls:delete') ?? false;
    if (poll.canViewResults)
      items.push({
        label: 'Results',
        onSelect: () => navigate(`/polls/${params.pollId}/results`),
      });
    if (poll.status !== 'draft' && poll.canManage)
      items.push({
        label: 'Edit description',
        onSelect: () => {
          setDescription(poll.description);
          setEditingDescription(true);
        },
      });
    if (
      poll.status === 'open' &&
      (session()?.permissions.has('polls:close') ?? false) &&
      (poll.canManage || canDeleteAny)
    )
      items.push({
        label: 'Close now',
        onSelect: async () => {
          if (!(await confirm({ title: `Close ${poll.title}?`, confirmLabel: 'Close' }))) return;
          await close.mutateAsync(undefined as never);
          toast.success('Poll closed');
        },
      });
    if (poll.status === 'open' && poll.myResponse && poll.allowEdit)
      items.push({
        label: 'Withdraw response',
        danger: true,
        onSelect: async () => {
          if (
            !(await confirm({
              title: 'Withdraw your response?',
              body: 'Your saved answers will be permanently removed.',
              confirmLabel: 'Withdraw',
              danger: true,
            }))
          )
            return;
          await retract.mutateAsync(undefined as never);
          toast.success('Response withdrawn');
        },
      });
    if (poll.canManage || canDeleteAny)
      items.push({
        label: 'Delete poll',
        danger: true,
        onSelect: async () => {
          if (
            !(await confirm({
              title: `Delete ${poll.title}?`,
              body: 'This permanently deletes every response.',
              confirmLabel: 'Delete',
              danger: true,
            }))
          )
            return;
          await remove.mutateAsync(undefined as never);
          navigate('/polls');
        },
      });
    return items.map((item) => ({
      ...item,
      disabled: publish.isPending || remove.isPending || close.isPending || retract.isPending,
    }));
  };

  return (
    <section class="flex min-h-full flex-col">
      <PageHeader
        title={query.data?.data.title ?? 'Poll'}
        sub={
          query.data ? (
            <Badge tone={query.data.data.status === 'open' ? 'success' : 'neutral'}>
              {query.data.data.status}
            </Badge>
          ) : undefined
        }
      />
      <div class="mx-auto w-full max-w-3xl flex-1 p-6">
        <Show when={!query.isPending} fallback={<Skeleton class="h-64" />}>
          <Show
            when={query.data?.data}
            fallback={<EmptyState variant="error" title="Poll not found" />}
          >
            {(poll) => {
              const parts = () => splitCover(poll().description);
              return (
                <Show when={poll().status !== 'draft'} fallback={<PollPreview poll={poll()} />}>
                  <Show when={parts().cover}>
                    {(cover) => (
                      <img
                        src={cover()}
                        alt={parts().alt}
                        class="mb-5 aspect-[16/5] w-full rounded-xl object-cover"
                      />
                    )}
                  </Show>
                  <Show when={parts().body}>
                    <Markdown source={parts().body} class="mb-5" />
                  </Show>
                  {/* Remount after submit/withdraw so the form starts from the saved answers. */}
                  <Show when={poll().myResponse?.id ?? 'none'} keyed>
                    <RespondForm poll={poll()} submitSlot={submitSlot()} />
                  </Show>
                </Show>
              );
            }}
          </Show>
        </Show>
      </div>
      <div class="sticky bottom-0 z-20 border-t border-neutral-200 bg-white/70 backdrop-blur-md backdrop-saturate-150 dark:border-neutral-800 dark:bg-surface-dark/70">
        <div class="mx-auto flex w-full max-w-3xl flex-wrap items-center gap-2 px-6 py-3">
          <A href="/polls">
            <Button variant="ghost">← Back</Button>
          </A>
          <span class="hidden flex-1 text-sm text-neutral-500 sm:block">
            <Show
              when={
                query.data?.data.status === 'open' &&
                query.data.data.myResponse &&
                query.data.data.allowEdit
              }
            >
              You can change your answers until the poll closes
            </Show>
          </span>
          <span class="flex-1 sm:hidden" />
          <Show when={query.data?.data.slug}>
            {(slug) => (
              <Button
                variant="secondary"
                onClick={() =>
                  copyPollLink(slug()).then(
                    () => toast.success('Link copied'),
                    () => toast.error('Could not copy link'),
                  )
                }
              >
                Copy link
              </Button>
            )}
          </Show>
          <Show when={actions().length}>
            <Menu
              trigger={
                <>
                  Actions <span aria-hidden="true">▾</span>
                </>
              }
              triggerClass={buttonClass('secondary')}
              items={actions()}
            />
          </Show>
          <div ref={setSubmitSlot} class="contents" />
        </div>
      </div>
      <Modal
        open={editingDescription()}
        onClose={() => setEditingDescription(false)}
        title="Edit description"
        size="xl"
      >
        <form
          class="flex flex-col gap-4"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await update.mutateAsync({ description: description() });
              setEditingDescription(false);
              toast.success('Description updated');
            } catch (error) {
              toast.error(
                error instanceof ApiClientError
                  ? error.error.message
                  : 'Could not update description',
              );
            }
          }}
        >
          <MarkdownEditor
            label="Description"
            placeholder="Add a description"
            tall
            value={description()}
            onChange={setDescription}
          />
          <div class="flex justify-end gap-2">
            <Button variant="ghost" type="button" onClick={() => setEditingDescription(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={update.isPending}>
              Save
            </Button>
          </div>
        </form>
      </Modal>
    </section>
  );
}
